import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '../../../components/ui/dialog';
import ImportantNotesDialog from '../../register/RegisterScreen/components/ImportantNotesDialog';
import { SelectCardBrandStep } from '../../register/RegisterScreen/components/SelectCardBrandStep';
import { SelectEWalletBrandStep } from '../../register/RegisterScreen/components/SelectEWalletBrandStep';
import { SelectPaymentTypeStep } from '../../register/RegisterScreen/components/SelectPaymentTypeStep';
import type { AddPaymentFlowHandlers } from '../hooks/useAddPaymentFlow';

interface AddPaymentMethodDialogProps extends AddPaymentFlowHandlers {
  open: boolean;
  onClose: () => void;
  isEWalletEnabled: boolean;
}

const AddPaymentMethodDialog: React.FC<AddPaymentMethodDialogProps> = ({
  open,
  onClose,
  isEWalletEnabled,
  step,
  selectedBrand,
  showImportantNotes,
  isRedirecting,
  isResolvingGateway,
  errorMessage,
  goToTypeSelection,
  // goToCardBrandSelection and goToEWalletBrandSelection are used via onSelectCards/onSelectEWallets
  goToCardBrandSelection,
  goToEWalletBrandSelection,
  confirmCardBrand,
  confirmEWalletBrand,
  closeImportantNotes,
  doAddPayment
}) => {
  const selectedCardBrand =
    selectedBrand &&
    ['visa', 'mastercard', 'amex', 'unionpay'].includes(selectedBrand)
      ? (selectedBrand as Parameters<typeof confirmCardBrand>[0])
      : null;

  const selectedEWalletBrand =
    selectedBrand && ['grabpay', 'tng', 'shopeepay'].includes(selectedBrand)
      ? (selectedBrand as Parameters<typeof confirmEWalletBrand>[0])
      : null;

  return (
    <>
      <Dialog open={open && !showImportantNotes} onOpenChange={onClose}>
        <DialogContent className="max-w-[25rem] rounded-3xl sm:max-w-[26rem]">
          <DialogHeader>
            <DialogTitle className="sr-only">Add Payment Method</DialogTitle>
          </DialogHeader>

          {isRedirecting ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="mb-4 h-10 w-10 animate-spin rounded-full border-4 border-[#5CB891] border-t-transparent" />
              <p className="text-sm font-medium text-gray-700">
                Redirecting...
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Please do not close or refresh the window.
              </p>
            </div>
          ) : errorMessage ? (
            <div className="px-6 py-8 text-center">
              <p className="text-sm text-red-600">{errorMessage}</p>
              <button
                type="button"
                onClick={onClose}
                className="mt-4 rounded-2xl bg-[#5CB891] px-6 py-3 text-sm font-semibold text-white hover:bg-[#4aa07c]"
              >
                Close
              </button>
            </div>
          ) : step === 'select-payment-type' ? (
            <SelectPaymentTypeStep
              onSelectCards={goToCardBrandSelection}
              onSelectEWallets={goToEWalletBrandSelection}
              onSkipPayment={onClose}
              onBack={onClose}
              isEWalletEnabled={isEWalletEnabled}
              backLabel={null}
              noTopMargin
            />
          ) : step === 'select-card-brand' ? (
            <SelectCardBrandStep
              initialBrand={selectedCardBrand}
              onConfirm={confirmCardBrand}
              onBack={goToTypeSelection}
              isLoading={isResolvingGateway}
              noTopMargin
            />
          ) : step === 'select-ewallet-brand' ? (
            <SelectEWalletBrandStep
              initialBrand={selectedEWalletBrand}
              onConfirm={confirmEWalletBrand}
              onBack={goToTypeSelection}
              isLoading={isResolvingGateway}
              noTopMargin
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <ImportantNotesDialog
        open={showImportantNotes}
        onClose={closeImportantNotes}
        onComplete={doAddPayment}
      />
    </>
  );
};

export default AddPaymentMethodDialog;
